'use client';

import React, { type FC, type ReactNode, type SelectHTMLAttributes } from 'react';
import { mergeProps, useFocusRing } from 'react-aria';
import { cx } from '../../../utils/cx';
import { SelectField, styles } from './SelectField';
import type { CommonProps, SelectItemType } from './Select';
import useId from '../../../utils/useId';

const SEMANTIC_NAME = 'RomaNativeSelect';

export type NativeSelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & {
    items?: SelectItemType[];
    leadingIcon?: FC | ReactNode;
} & CommonProps;

export const NativeSelect = ({
    label,
    hint,
    items = [],
    placeholder,
    tooltip,
    leadingIcon,
    className,
    size = 'md',
    ...props
}: NativeSelectProps) => {
    const uniqueId = useId();
    const selectId = `${uniqueId}-select`;
    const labelId = `${uniqueId}-label`;
    const hintId = `${uniqueId}-hint`;

    const placeholderLabel = typeof placeholder === 'string' ? placeholder : 'Select';
    const isInvalid =
        props['aria-invalid'] !== undefined && props['aria-invalid'] !== false && props['aria-invalid'] !== 'false';
    const { focusProps, isFocusVisible } = useFocusRing();

    return (
        <SelectField
            label={label}
            hint={hint}
            tooltip={tooltip}
            labelFor={selectId}
            labelId={labelId}
            hintId={hintId}
            isRequired={props.required}
            isInvalid={isInvalid}
            isDisabled={props.disabled}
            size={size}
            leadingIcon={leadingIcon}
            className={cx(SEMANTIC_NAME, className)}
        >
            <select
                {...mergeProps(props, focusProps)}
                id={selectId}
                data-focus-visible={isFocusVisible || undefined}
                aria-describedby={hint ? hintId : undefined}
                aria-labelledby={labelId}
                className={cx(
                    styles.common.control,
                    styles.sizes[size].root,
                    leadingIcon ? styles.sizes[size].leadingPadding : styles.sizes[size].defaultPadding,
                    styles.sizes[size].trailingPadding,
                )}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholderLabel}
                    </option>
                )}

                {items.map((item) => (
                    <option key={item.id} value={item.id} disabled={item.isDisabled}>
                        {item.label}
                    </option>
                ))}
            </select>
        </SelectField>
    );
};

NativeSelect.displayName = SEMANTIC_NAME;
